import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateCategory } from "@/hooks/use-categories";
import { useJobTypes } from "@/hooks/use-job-types";
import type { CreateCategoryInput } from "@/schemas/categories";
import { CreateCategorySchema } from "@/schemas/categories";

interface NewCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewCategoryDialog({ open, onOpenChange }: NewCategoryDialogProps) {
  const createCategory = useCreateCategory();
  const { data: jobTypes = [] } = useJobTypes();

  const form = useForm<CreateCategoryInput>({
    resolver: zodResolver(CreateCategorySchema),
    defaultValues: { name: "", description: "", jobTypeIds: [], active: true },
  });

  function toggleJobType(id: string) {
    const current = form.getValues("jobTypeIds");
    if (current.includes(id)) {
      form.setValue(
        "jobTypeIds",
        current.filter((x) => x !== id),
        { shouldValidate: true },
      );
    } else {
      form.setValue("jobTypeIds", [...current, id], { shouldValidate: true });
    }
  }

  function onSubmit(values: CreateCategoryInput) {
    createCategory.mutate(values, {
      onSuccess: () => {
        form.reset();
        onOpenChange(false);
      },
    });
  }

  const selectedIds = form.watch("jobTypeIds");
  const activeJobTypes = jobTypes.filter((jt) => jt.active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Category</DialogTitle>
          <DialogDescription>
            Create a new activity category and assign it to one or more job types.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Report Writing" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description{" "}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add a description…"
                      className="resize-none"
                      rows={2}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="jobTypeIds"
              render={() => (
                <FormItem>
                  <FormLabel>Job Types</FormLabel>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {activeJobTypes.map((jt) => {
                      const selected = selectedIds.includes(jt.id);
                      return (
                        <button
                          key={jt.id}
                          type="button"
                          onClick={() => toggleJobType(jt.id)}
                          className={`chip-jobtype chip-jobtype--${jt.code} cursor-pointer transition-opacity ${
                            selected ? "ring-2 ring-offset-1 ring-ring opacity-100" : "opacity-50"
                          }`}
                          title={jt.label}
                        >
                          {jt.code}
                        </button>
                      );
                    })}
                    {activeJobTypes.length === 0 && (
                      <p className="text-sm text-muted-foreground">No active job types found.</p>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createCategory.isPending}>
                {createCategory.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
